import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { fetchDesigns, deleteDesigns, exportAsWorkplaceDesigns, importDesigns, exportDesignsAsPDF, Design as ServiceDesign } from '../../services/designsService';
import { fetchLocations, Place } from '../../services/locationService';
import { formatLastModified } from '../../utilities/dateFormat';
import { 
  Box, 
  Typography, 
  Button, 
  IconButton,
  InputBase,
  Tooltip,
  CircularProgress
} from '@mui/material';
import DesignsTable from './DesignsTable';
import DesignsThumbnail from './DesignsThumbnail';
import BottomPanel from '../organisms/BottomPanel';
import ShareDialog from '../organisms/ShareDialog';
import DesignerHeader from '../organisms/DesignerHeader';
import ModalManager from '../services/ModalManager';
import ToolContext from '../services/ToolContext';
import WalkthroughGuide from '../organisms/WalkthroughGuide';
import ReducersIndex from '../../reduxModules/ducks';
import toolIndex from '../../toolConstants';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export interface Design extends ServiceDesign {
  selected: boolean;
  uniqueKey: string; // Combination of id and revision for unique identification
}

export type SortField = 'project' | 'location' | 'objectType' | 'revision' | 'designer' | 'updatedAt';
export type SortDirection = 'asc' | 'desc';

const FETCH_ALL_LIMIT = 10000;

const DesignsList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { signout, user } = useAuth();
  const { showSuccess, showError, showConfirm } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);

  // Initialize view mode from localStorage, default to 'thumbnail' if not found
  const [viewMode, setViewMode] = useState<'table' | 'thumbnail'>(() => {
    const savedViewMode = localStorage.getItem('designsViewMode');
    return (savedViewMode === 'table' || savedViewMode === 'thumbnail')
      ? savedViewMode
      : 'thumbnail';
  });

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedOrgName, setSelectedOrgName] = useState<string>('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [designs, setDesigns] = useState<Design[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [locations, setLocations] = useState<Place[]>([]);
  
  // Get the conference room designer tool for tutorial context
  const tool = toolIndex.conferenceroomDesigner || Object.values(toolIndex)[0];

  // Load selected organization from sessionStorage
  useEffect(() => {
    const orgName = sessionStorage.getItem('selectedOrgName');
    const orgId = sessionStorage.getItem('selectedOrgId');
    
    console.log('DesignsList: Checking organization', { orgName, orgId });
    
    // Check if authentication is disabled for dev mode
    const isAuthDisabled = process.env.REACT_APP_DISABLE_AUTH === 'true';
    
    if (orgName && orgId) {
      console.log('DesignsList: Organization found, setting state');
      setSelectedOrgName(orgName);
      setSelectedOrgId(orgId);
    } else if (isAuthDisabled) {
      // In dev-no-auth mode, use hardcoded organization
      console.log('DesignsList: Auth disabled, using dev organization');
      setSelectedOrgName('Dev Organization');
      setSelectedOrgId('dev-org-123');
    } else {
      // If no organization is selected, redirect to SelectOrg
      console.log('DesignsList: No organization found, redirecting to /selectorg');
      navigate('/selectorg');
    }
  }, [navigate]);

  // Show welcome guide when component mounts (only for first-time users)
  useEffect(() => {
    // TESTING: Always show walkthrough - disabled localStorage check
    // const hasCompleted = localStorage.getItem('hasCompletedTutorial') === 'true';
    const hasCompleted = false; // TESTING: Always treat as not completed
    
    if (!hasCompleted) {
      // Delay slightly to ensure component is fully rendered
      const timer = setTimeout(() => {
        if (tool?.tutorialSteps) {
          dispatch(ReducersIndex.uiReducers.setTutorialConfig(tool.tutorialSteps));
          // Don't open the modal - WalkthroughGuide will handle it
          // dispatch(ReducersIndex.uiReducers.openModal('tutorial'));
          dispatch(ReducersIndex.uiReducers.setTutorialStep(0)); // Start from welcome screen
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [dispatch, tool]);

  // Load locations when organization is selected
  useEffect(() => {
    const loadLocations = async () => {
      if (!selectedOrgId) {
        return;
      }
      
      try {
        const fetchedLocations = await fetchLocations(selectedOrgId);
        setLocations(fetchedLocations);
      } catch (err) {
        console.error('Error loading locations:', err);
        // Continue without locations - will show locationId as fallback
      }
    };

    loadLocations();
  }, [selectedOrgId]);

  // Load designs when organization is selected
  useEffect(() => {
    const loadDesigns = async () => {
      if (!selectedOrgId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        // Don't pass searchTerm to fetchDesigns - we'll filter locally
        const response = await fetchDesigns(selectedOrgId, 1, FETCH_ALL_LIMIT);
        
        // Add selected field and unique key to each design
        const designsWithSelection = response.designs.map(design => ({
          ...design,
          selected: false,
          uniqueKey: `${design.id}-${design.revision}` // Unique identifier combining id and revision
        }));
        
        setDesigns(designsWithSelection);
      } catch (err) {
        console.error('Error loading designs:', err);
        setError('Failed to load designs. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadDesigns();
  }, [selectedOrgId]); // Removed searchTerm dependency

  // Listen for designs imported event to refresh the list
  useEffect(() => {
    const handleDesignsImported = () => {
      // Refresh the designs list when import is successful
      if (selectedOrgId) {
        const loadDesigns = async () => {
          try {
            setLoading(true);
            setError(null);
            const response = await fetchDesigns(selectedOrgId, 1, FETCH_ALL_LIMIT);
            
            // Add selected field and unique key to each design
            const designsWithSelection = response.designs.map(design => ({
              ...design,
              selected: false,
              uniqueKey: `${design.id}-${design.revision}` // Unique identifier combining id and revision
            }));
            
            setDesigns(designsWithSelection);
          } catch (err) {
            console.error('Error loading designs after import:', err);
            setError('Failed to load designs. Please try again.');
          } finally {
            setLoading(false);
          }
        };

        loadDesigns();
      }
    };

    // Add event listener for designs imported
    window.addEventListener('designsImported', handleDesignsImported);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('designsImported', handleDesignsImported);
    };
  }, [selectedOrgId, searchTerm]);

  const selectedCount = designs.filter(design => design.selected).length;
  const selectedDesign = designs.find(design => design.selected);

  // Helper function to get location name by ID
  const getLocationName = (locationId: string): string => {
    if (!locationId) return '';
    const location = locations.find(loc => loc.id === locationId);
    if (location) {
      // Build the full path for the location
      const pathParts = location.hierarchy.map(h => h.name);
      return pathParts.join(' / ');
    }
    return locationId; // Fallback to showing ID if location not found
  };

  const handleSelectAll = (checked: boolean) => {
    setDesigns(designs.map(design => ({ ...design, selected: checked })));
  };

  const handleSelectDesign = (uniqueKey: string, checked: boolean) => {
    setDesigns(designs.map(design => 
      design.uniqueKey === uniqueKey ? { ...design, selected: checked } : design
    ));
  };

  const handleDeleteSelected = async () => {
    const selectedDesigns = designs.filter(design => design.selected);
    if (selectedDesigns.length === 0) return;

    // Create confirmation message with count only
    const confirmMessage = selectedDesigns.length === 1
      ? 'Are you sure you want to delete this design? This is a permanent action and can not be undone.'
      : `Are you sure you want to delete ${selectedDesigns.length} designs? This is a permanent action and can not be undone.`;

    // Show confirmation dialog
    const confirmed = await showConfirm({
      title: 'Delete Designs',
      message: confirmMessage,
      type: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      showIcon: false
    });
    
    if (confirmed) {
      try {
        const selectedIds = selectedDesigns.map(design => design.id);
        await deleteDesigns(selectedIds);
        
        // Remove deleted designs from local state
        setDesigns(prevDesigns => 
          prevDesigns.filter(design => !design.selected)
        );
        
        // Show success toast
        const successMessage = selectedDesigns.length === 1 
          ? `Design "${selectedDesigns[0].project}" deleted successfully`
          : `${selectedDesigns.length} designs deleted successfully`;
        showSuccess(successMessage);
        
      } catch (error) {
        console.error('Error deleting designs:', error);
        showError('Failed to delete designs. Please try again.');
      }
    }
  };

  const handleExportPDF = async () => {
    const selectedDesigns = designs.filter(design => design.selected);
    if (selectedDesigns.length === 0) return;

    try {
      // Log all data for selected designs
      // Show loading state or progress indicator
      const selectedIds = selectedDesigns.map(design => design.id);
      
      // Call the enhanced export service function that generates visual PDFs
      const blob = await exportDesignsAsPDF(selectedIds, selectedOrgId);
      
      // Create download link and trigger download with proper MIME type
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      
      // Force download behavior
      link.setAttribute('download', ''); // Ensure download attribute exists
      link.style.display = 'none'; // Hide the link
      
      // Generate filename based on selection
      const filename = selectedDesigns.length === 1 
        ? `${selectedDesigns[0].project}_${selectedDesigns[0].revision}.pdf`
        : `designs_export_${new Date().getTime()}.pdf`;
      
      link.download = filename;

      // Trigger download with proper event handling
      document.body.appendChild(link);
      
      // Force click event to trigger download
      if (link.click) {
        link.click();
      } else {
        // Fallback for older browsers
        const event = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        link.dispatchEvent(event);
      }

      // Cleanup with slight delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
    } catch (error) {
      console.error('Error exporting designs as PDF:', error);
      showError('Failed to export designs as PDF. Please try again.');
    }
  };

  const handleExport = async () => {
    const selectedDesigns = designs.filter(design => design.selected);
    if (selectedDesigns.length === 0) return;

    try {
      const selectedIds = selectedDesigns.map(design => design.id);
      await exportAsWorkplaceDesigns(selectedIds, () => {
        // Show success toast after download is saved
        showSuccess('Designs exported successfully!');
      });
      
    } catch (error) {
      console.error('Error exporting designs:', error);
      showError('Failed to export designs. Please try again.');
    }
  };

  const handleNewDesign = () => {
    // Clear all Redux state by reloading the page with a fresh conference room designer
    window.location.href = '/conference_room_designer';
  };

  const handleEditDesignByClick = (design: Design) => {
    // Navigate to conference room designer with the clicked design's ID and ver=0
    navigate(`/conference_room_designer?savekey=${design.id}&ver=0`);
  };

  const handleImport = () => {
    // Trigger file input click
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // Call the import service
      await importDesigns(file, selectedOrgId);
      
      // Show success message
      showSuccess('Designs imported successfully!');
      
      // Refresh the designs list
      const response = await fetchDesigns(selectedOrgId, 1, FETCH_ALL_LIMIT, searchTerm);
      const designsWithSelection = response.designs.map(design => ({
        ...design,
        selected: false,
        uniqueKey: `${design.id}-${design.revision}`
      }));
      setDesigns(designsWithSelection);
      
    } catch (error) {
      console.error('Error importing designs:', error);
      showError(`Failed to import designs: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      // Reset the file input so the same file can be selected again if needed
      if (event.target) {
        event.target.value = '';
      }
    }
  };


  const handleSignout = async () => {
    try {
      // Call the sign out function from AuthContext
      await signout();
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  };

  const handleShare = () => {
    const selectedDesigns = designs.filter(design => design.selected);
    if (selectedDesigns.length === 0) return;
    
    // Open the share dialog
    setShareDialogOpen(true);
  };

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
  };

  const handleTransfer = async (organizationId: string, locationId: string) => {
    const selectedDesigns = designs.filter(design => design.selected);
    if (selectedDesigns.length === 0) return;

    try {
      // TODO: Implement actual transfer API call
      
      showSuccess(`Successfully transferred ${selectedDesigns.length} design${selectedDesigns.length !== 1 ? 's' : ''}`);
      
      // Close dialog and refresh designs list
      setShareDialogOpen(false);
      
      // Optionally refresh the designs list after transfer
      const response = await fetchDesigns(selectedOrgId, 1, FETCH_ALL_LIMIT, searchTerm);
      const designsWithSelection = response.designs.map(design => ({
        ...design,
        selected: false,
        uniqueKey: `${design.id}-${design.revision}`
      }));
      setDesigns(designsWithSelection);
    } catch (error) {
      console.error('Error transferring designs:', error);
      showError('Failed to transfer designs. Please try again.');
    }
  };

  const handleShareDesigns = async (organizationId: string, locationId: string) => {
    const selectedDesigns = designs.filter(design => design.selected);
    if (selectedDesigns.length === 0) return;

    try {
      // TODO: Implement actual share API call
      
      showSuccess(`Successfully shared ${selectedDesigns.length} design${selectedDesigns.length !== 1 ? 's' : ''}`);
      
      // Close dialog
      setShareDialogOpen(false);
      
      // Refresh the designs list after sharing to show any new copies
      const response = await fetchDesigns(selectedOrgId, 1, FETCH_ALL_LIMIT, searchTerm);
      const designsWithSelection = response.designs.map(design => ({
        ...design,
        selected: false,
        uniqueKey: `${design.id}-${design.revision}`
      }));
      setDesigns(designsWithSelection);
    } catch (error) {
      console.error('Error sharing designs:', error);
      showError('Failed to share designs. Please try again.');
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different field, set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getFilteredAndSortedDesigns = () => {
    let filteredDesigns = [...designs];

    // Apply search filter if provided
    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredDesigns = filteredDesigns.filter((design) => {
        // Get the location name for searching
        const locationName = getLocationName(design.locationId).toLowerCase();

        // Format the date for search comparison
        const formattedDate = formatLastModified(design.updatedAt).toLowerCase();

        // Search in all relevant fields
        return (
          design.project.toLowerCase().includes(searchLower) ||
          locationName.includes(searchLower) ||
          design.designer.toLowerCase().includes(searchLower) ||
          design.designType.toLowerCase().includes(searchLower) ||
          formattedDate.includes(searchLower) ||
          design.id.toLowerCase().includes(searchLower)
        );
      });
    }

    // Sort the filtered results only if a sort field is selected
    if (sortField) {
      return filteredDesigns.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Handle date sorting for updatedAt
        if (sortField === 'updatedAt') {
          aValue = new Date(aValue).getTime().toString();
          bValue = new Date(bValue).getTime().toString();
        }

        // Handle null/undefined values - treat them as empty strings for sorting
        const safeAValue = aValue?.toString() || '';
        const safeBValue = bValue?.toString() || '';

        if (sortDirection === 'asc') {
          return safeAValue.localeCompare(safeBValue);
        } else {
          return safeBValue.localeCompare(safeAValue);
        }
      });
    }

    // Return unsorted filtered results
    return filteredDesigns;
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpwardIcon sx={{ width: '8px', height: '12px', opacity: 0.3 }} />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUpwardIcon sx={{ width: '8px', height: '12px' }} />
      : <ArrowDownwardIcon sx={{ width: '8px', height: '12px' }} />;
  };

  const allSelected = designs.length > 0 && designs.every(design => design.selected);
  const someSelected = designs.some(design => design.selected);

  return (
    <ToolContext.Provider value={tool}>
      <MainContainer>
        {/* Main Content */}
        <ContentArea>
        {/* Header */}
        <DesignerHeader showMyDesigns={false} />

        {/* Search and View Controls */}
        <ControlsContainer>
          <ViewControls>
            <ViewButton
              active={viewMode === 'thumbnail'}
              onClick={() => {
                setViewMode('thumbnail');
                localStorage.setItem('designsViewMode', 'thumbnail');
              }}
            >
              Grid view
            </ViewButton>
            <ViewButton
              active={viewMode === 'table'}
              onClick={() => {
                setViewMode('table');
                localStorage.setItem('designsViewMode', 'table');
              }}
            >
              List view
            </ViewButton>
          </ViewControls>
          
          <SearchContainer expanded={searchExpanded} onClick={() => setSearchExpanded(!searchExpanded)}>
            <SearchIcon sx={{ color: theme.palette.text.secondary, width: '16px', height: '16px' }} />
            {searchExpanded && (
              <SearchInput
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            )}
          </SearchContainer>
        </ControlsContainer>

        {/* Content based on viewMode */}
        <TableWrapper>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '200px', gap: '16px' }}>
              <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
              <Typography sx={{ color: theme.palette.text.secondary, fontSize: '14px' }}>Loading designs...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : viewMode === 'table' ? (
            <DesignsTable
              designs={getFilteredAndSortedDesigns()}
              allSelected={allSelected}
              someSelected={someSelected}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSelectAll={handleSelectAll}
              handleSelectDesign={handleSelectDesign}
              handleSort={handleSort}
              handleEditDesignByClick={handleEditDesignByClick}
              getLocationName={getLocationName}
            />
          ) : viewMode === 'thumbnail' ? (
            <DesignsThumbnail
              designs={getFilteredAndSortedDesigns()}
              allSelected={allSelected}
              someSelected={someSelected}
              handleSelectAll={handleSelectAll}
              handleSelectDesign={handleSelectDesign}
              handleEditDesignByClick={handleEditDesignByClick}
              organizationId={selectedOrgId}
              getLocationName={getLocationName}
              onDesignUpdate={(updatedDesign) => {
                setDesigns(prevDesigns => 
                  prevDesigns.map(design => 
                    design.uniqueKey === updatedDesign.uniqueKey 
                      ? updatedDesign 
                      : design
                  )
                );
              }}
              onDesignDelete={(designId) => {
                setDesigns(prevDesigns =>
                  prevDesigns.filter(design => design.id !== designId)
                );
              }}
              onDesignDuplicate={async () => {
                // Refresh the designs list after duplication
                try {
                  const response = await fetchDesigns(selectedOrgId, 1, FETCH_ALL_LIMIT);
                  const designsWithSelection = response.designs.map(design => ({
                    ...design,
                    selected: false,
                    uniqueKey: `${design.id}-${design.revision}`
                  }));
                  setDesigns(designsWithSelection);
                } catch (err) {
                  console.error('Error refreshing designs after duplication:', err);
                  showError('Failed to refresh designs list. Please refresh the page.');
                }
              }}
            />
          ) : null}
        </TableWrapper>

        {/* Hidden file input for import functionality */}
        <input
          type="file"
          ref={fileInputRef}
          accept=".bwd"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

      </ContentArea>

      {/* Bottom Panel */}
      <BottomPanel
        selectedCount={selectedCount}
        onExport={handleExport}
        onExportPDF={handleExportPDF}
        onImport={handleImport}
        onDelete={handleDeleteSelected}
        onSignout={handleSignout}
        onNewDesign={handleNewDesign}
        onShare={handleShare}
      />

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onClose={handleShareDialogClose}
        onTransfer={handleTransfer}
        onShare={handleShareDesigns}
        selectedDesignIds={designs.filter(d => d.selected).map(d => d.id)}
        onSuccess={async (message, type) => {
          // Show success message
          showSuccess(message);
          
          // Refresh the designs list to show updated content
          try {
            const response = await fetchDesigns(selectedOrgId, 1, FETCH_ALL_LIMIT);
            const designsWithSelection = response.designs.map(design => ({
              ...design,
              selected: false,
              uniqueKey: `${design.id}-${design.revision}`
            }));
            setDesigns(designsWithSelection);
          } catch (error) {
            console.error('Error refreshing designs list:', error);
          }
        }}
        onError={(error, type) => {
          showError(error);
        }}
      />
      
      {/* Modal Manager for Tutorial */}
      <ModalManager top={0} />
      
      {/* WalkthroughGuide for Tutorial */}
      <WalkthroughGuide showOnDesignsPage={true} />
    </MainContainer>
    </ToolContext.Provider>
  );
};

// Styled Components
const MainContainer = styled(Box)(({ theme }) => ({
  width: '100vw',
  height: '100vh',
  position: 'relative',
  overflow: 'hidden',
  background: theme.palette.background.default,
  margin: 0,
}));


const ContentArea = styled(Box)(({ theme }) => ({
  height: '100%',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  background: theme.palette.background.default,
  paddingBottom: '60px', // Account for bottom panel height
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  padding: '20px 40px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 2px 4px rgba(0, 0, 0, 0.3)'
    : '0 2px 4px rgba(0, 0, 0, 0.05)',
}));

interface SearchContainerProps {
  expanded?: boolean;
}

const SearchContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'expanded'
})<SearchContainerProps>(({ expanded, theme }) => ({
  width: expanded ? '299px' : '44px',
  height: '44px',
  background: expanded ? theme.palette.background.paper : 'transparent',
  border: expanded ? `1px solid ${theme.palette.divider}` : 'none',
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  alignItems: 'center',
  padding: '0 12px',
  gap: '8px',
  cursor: expanded ? 'default' : 'pointer',
  transition: 'all 0.3s ease-in-out',
  overflow: 'hidden',
  '&:hover': {
    cursor: expanded ? 'default' : 'pointer',
    backgroundColor: expanded ? theme.palette.background.paper : theme.palette.action.hover,
    border: expanded ? `1px solid ${theme.palette.primary.main}` : 'none',
  },
  '&:focus-within': {
    border: expanded ? `1px solid ${theme.palette.primary.main}` : 'none',
    boxShadow: expanded ? `0 0 0 2px ${theme.palette.primary.main}20` : 'none',
  },
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  fontSize: '14px',
  color: theme.palette.text.primary,
  '& input': {
    padding: 0,
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.7,
    },
  },
  '& .MuiInputBase-input': {
    padding: 0,
  },
}));

const ViewControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '4px',
  background: theme.palette.mode === 'dark'
    ? theme.palette.grey[700]
    : theme.palette.grey[50],
  borderRadius: '8px',
  padding: '4px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark'
    ? 'inset 0 1px 3px rgba(0, 0, 0, 0.4)'
    : 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
}));

interface ViewButtonProps {
  active?: boolean;
}

const ViewButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active'
})<ViewButtonProps>(({ active, theme }) => ({
  height: '28px',
  padding: '0 12px',
  borderRadius: theme.shape.borderRadius,
  border: 'none',
  background: active 
    ? theme.palette.background.default 
    : 'transparent',
  overflow: 'hidden',
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  textOverflow: 'ellipsis',
  fontFamily: '"Open Sans"',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: active ? 600 : 500,
  lineHeight: '150%',
  letterSpacing: '-0.28px',
  fontFeatureSettings: "'liga' off, 'clig' off",
  textTransform: 'none',
  minWidth: 'auto',
  boxShadow: active 
    ? theme.palette.mode === 'dark'
      ? '0px 1px 3px rgba(0, 0, 0, 0.3)'
      : '0px 1px 2px rgba(0, 0, 0, 0.1)'
    : 'none',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: active 
      ? theme.palette.background.default 
      : theme.palette.action.hover,
    color: active ? theme.palette.text.primary : theme.palette.text.primary,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
}));


const TableWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
  padding: '20px 40px',
  maxHeight: 'calc(100vh - 260px)', // Adjust based on header and controls height
  overflow: 'auto',
  '&::-webkit-scrollbar': {
    width: '10px',
    height: '10px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[100],
    borderRadius: '5px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark'
      ? theme.palette.grey[600]
      : theme.palette.grey[400],
    borderRadius: '5px',
    border: `2px solid ${theme.palette.mode === 'dark' 
      ? theme.palette.grey[800] 
      : theme.palette.grey[100]}`,
    '&:hover': {
      background: theme.palette.mode === 'dark'
        ? theme.palette.grey[500]
        : theme.palette.grey[500],
    },
  },
  '&::-webkit-scrollbar-corner': {
    background: 'transparent',
  },
}));

export default DesignsList;
